/**
 * Query String Builder
 * Converts query objects to URL query strings for API calls
 */

interface QueryObject {
  [key: string]: any;
}

export function buildQueryString(query?: QueryObject): string {
  if (!query || Object.keys(query).length === 0) {
    return '';
  }

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      // Handle different value types
      if (typeof value === 'object' && !Array.isArray(value)) {
        // For nested objects like date ranges, convert to JSON
        params.append(key, JSON.stringify(value));
      } else if (Array.isArray(value)) {
        // Handle arrays
        value.forEach((v) => params.append(key, v));
      } else {
        params.append(key, String(value));
      }
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Build Payload CMS Query Syntax
 * Creates proper query strings for Payload CMS endpoints
 */
export function buildPayloadQuery(options: {
  where?: Record<string, any>;
  limit?: number;
  page?: number;
  sort?: string;
}): string {
  const params = new URLSearchParams();

  if (options.limit) {
    params.append('limit', String(options.limit));
  }

  if (options.page) {
    params.append('page', String(options.page));
  }

  if (options.sort) {
    params.append('sort', options.sort);
  }

  // Build where clauses for Payload CMS syntax
  if (options.where) {
    Object.entries(options.where).forEach(([field, value]) => {
      if (typeof value === 'object') {
        // Handle operators like equals, contains, etc.
        Object.entries(value).forEach(([operator, operatorValue]) => {
          params.append(`where[${field}][${operator}]`, String(operatorValue));
        });
      } else {
        params.append(`where[${field}][equals]`, String(value));
      }
    });
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Format date for API queries
 */
export function formatDateForAPI(date: Date): string {
  return date.toISOString();
}

/**
 * Parse API query response
 */
export function parseQueryParams(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}
